import React, { useState, useEffect } from 'react';
import { formsAPI } from '../../utils/crmApi';
import toast from 'react-hot-toast';
import { cn } from '../../lib/utils';
import { useBranding } from '../../contexts/BrandingContext';
import { useDialog } from '../kibo-ui/dialog';

const FormShareModal = ({ isOpen, onClose, form }) => {
  if (!isOpen) return null;
  const { branding } = useBranding();
  const { openDialog } = useDialog();
  const base = process.env.REACT_APP_FRONTEND_URL || window.location.origin;
  const initialSlug = form?.slug || '';
  const initialAccess = form?.access || { visibility: 'public', allowSaveResume: false };
  const [slug, setSlug] = useState(initialSlug);
  const [access, setAccess] = useState(initialAccess);
  const [saving, setSaving] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  
  // Update slug and access state when form prop changes
  useEffect(() => {
    if (form?.slug !== undefined) {
      setSlug(form.slug || '');
    }
    if (form?.access) {
      setAccess(form.access);
    }
    setHasUnsavedChanges(false);
  }, [form?.slug, form?.access]);
  
  // Generate link from current slug (which might be unsaved)
  const link = slug ? `${base}/public/forms/${encodeURIComponent(slug)}` : null;
  const iframe = link ? `<iframe src="${link}" width="100%" height="600" style="border:0"></iframe>` : '';
  
  // Check for unsaved changes (compare slug and access, without password field)
  useEffect(() => {
    const currentAccess = { ...access };
    const initialAccessCopy = { ...initialAccess };
    // Remove password from comparison since it's not persisted in the same way
    delete currentAccess.password;
    delete initialAccessCopy.password;
    const accessChanged = JSON.stringify(currentAccess) !== JSON.stringify(initialAccessCopy);
    const slugChanged = slug !== initialSlug;
    setHasUnsavedChanges(accessChanged || slugChanged);
  }, [access, slug, initialAccess, initialSlug]);
  const [copiedLink, setCopiedLink] = useState(false);
  const [copiedEmbed, setCopiedEmbed] = useState(false);
  
  const copyToClipboard = async (text, type) => {
    try {
      await navigator.clipboard.writeText(text);
      if (type === 'link') {
        setCopiedLink(true);
        setTimeout(() => setCopiedLink(false), 2000);
      } else {
        setCopiedEmbed(true);
        setTimeout(() => setCopiedEmbed(false), 2000);
      }
    } catch (err) {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = text;
      textArea.style.position = 'fixed';
      textArea.style.opacity = '0';
      document.body.appendChild(textArea);
      textArea.select();
      try {
        document.execCommand('copy');
        if (type === 'link') {
          setCopiedLink(true);
          setTimeout(() => setCopiedLink(false), 2000);
        } else {
          setCopiedEmbed(true);
          setTimeout(() => setCopiedEmbed(false), 2000);
        }
      } catch (e) {
        // Silent fail
      }
      document.body.removeChild(textArea);
    }
  };
  const handleClose = () => {
    if (hasUnsavedChanges) {
      openDialog({
        variant: 'warning',
        title: 'Unsaved changes',
        description: 'You have unsaved changes. Are you sure you want to close? Your changes will be lost.',
        confirmText: 'Discard changes',
        cancelText: 'Cancel',
        onConfirm: () => {
          // Reset to initial values
          setSlug(initialSlug);
          setAccess(initialAccess);
          setHasUnsavedChanges(false);
          onClose();
        },
        onClose: () => {}
      });
    } else {
      onClose();
    }
  };

  const saveAccess = async () => {
    try {
      setSaving(true);
      
      // Validate password if visibility is password
      if (access.visibility === 'password' && !access.password?.trim()) {
        toast.error('Please enter a password');
        return;
      }
      
      // Validate slug if form is published
      if (form?.status === 'published' && !slug?.trim()) {
        toast.error('Please enter a slug for the form');
        return;
      }
      
      // Normalize slug: lowercase, replace spaces/special chars with hyphens
      let normalizedSlug = slug.trim().toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
      if (!normalizedSlug && form?.status === 'published') {
        // Generate a default slug if empty
        const base = (form.name || 'form').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
        normalizedSlug = `${base}-${form.version || 1}`;
      }
      
      // Send both slug and access
      const updateData = {
        access,
        ...(form?.status === 'published' && normalizedSlug && { slug: normalizedSlug })
      };
      
      const result = await formsAPI.update(form._id, updateData);
      
      // Update initial state to reflect saved state (without password)
      const savedAccess = { ...access };
      if (savedAccess.visibility === 'password') {
        delete savedAccess.password; // Clear password from local state after save
      }
      setAccess(savedAccess);
      if (result?.data?.data?.slug) {
        setSlug(result.data.data.slug);
      } else if (normalizedSlug) {
        setSlug(normalizedSlug);
      }
      setHasUnsavedChanges(false);
      
      toast.success('Settings saved successfully');
    } catch (e) {
      console.error('Failed to save settings:', e);
      toast.error(e.response?.data?.message || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };
  return (
    <div className="fixed inset-0 z-[120] flex items-center justify-center bg-black/50" onClick={handleClose}>
      <div className="bg-card w-full max-w-2xl rounded-lg shadow p-4" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-lg font-semibold text-foreground">Share & Embed</h2>
          <button className="px-2 py-1 text-sm hover:bg-muted rounded transition-colors" onClick={handleClose}>Close</button>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <div className="space-y-3">
              {form?.status === 'published' && (
                <div>
                  <div className="text-sm font-medium text-foreground mb-1">URL Slug</div>
                  <input 
                    className="w-full px-3 py-2 border rounded font-mono text-sm bg-background text-foreground border-input focus:outline-none focus:ring-2 focus:ring-ring focus:border-ring" 
                    type="text" 
                    placeholder="form-slug"
                    value={slug || ''} 
                    onChange={(e) => {
                      // Allow alphanumeric, hyphens, underscores; normalize to lowercase
                      const value = e.target.value.toLowerCase().replace(/[^a-z0-9_-]/g, '');
                      setSlug(value);
                    }}
                  />
                  <div className="text-xs text-muted-foreground mt-1">
                    Used in the public URL: <code className="bg-muted px-1 rounded">/public/forms/{slug || '...'}</code>
                  </div>
                </div>
              )}
              <div>
                <div className="text-sm font-medium text-foreground mb-1">Access</div>
                <select className="w-full px-3 py-2 border rounded bg-background text-foreground border-input focus:outline-none focus:ring-2 focus:ring-ring focus:border-ring" value={access.visibility} onChange={(e) => setAccess({ ...access, visibility: e.target.value })}>
                  <option value="public">Public</option>
                  <option value="password">Password</option>
                  <option value="signed">Signed link</option>
                  <option value="client-auth">Client portal</option>
                </select>
              </div>
              {access.visibility === 'password' && (
                <div>
                  <div className="text-sm text-muted-foreground mb-1">Password</div>
                  <input className="w-full px-3 py-2 border rounded bg-background text-foreground border-input focus:outline-none focus:ring-2 focus:ring-ring focus:border-ring" type="password" placeholder="Set a password" value={access.password || ''} onChange={(e) => setAccess({ ...access, password: e.target.value })} />
                </div>
              )}
              <div className="flex items-center gap-2">
                <input id="saveResume" type="checkbox" checked={!!access.allowSaveResume} onChange={(e) => setAccess({ ...access, allowSaveResume: e.target.checked })} />
                <label htmlFor="saveResume" className="text-sm text-foreground">Allow save & resume</label>
              </div>
              {form?.status !== 'published' && (
                <div className="text-xs text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-950/10 border border-amber-200 dark:border-amber-800 rounded px-2 py-1.5">
                  ⚠️ Form must be published to generate a public link
                </div>
              )}
              {hasUnsavedChanges && (
                <div className="text-xs text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-950/10 border border-red-200 dark:border-red-800 rounded px-2 py-1.5">
                  ⚠️ You have unsaved changes. Click "Save" to apply them.
                </div>
              )}
              <div className="text-right">
                <button 
                  disabled={saving} 
                  className={cn("px-3 py-2 text-white rounded hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors", hasUnsavedChanges && "hover:bg-red-700")} 
                  style={{ backgroundColor: hasUnsavedChanges ? '#dc2626' : (branding?.primaryColor || '#3b82f6') }}
                  onClick={saveAccess}
                >
                  {saving ? 'Saving…' : hasUnsavedChanges ? 'Save (Unsaved)' : 'Save'}
                </button>
              </div>
            </div>
          </div>
          <div>
            {link ? (
              <div className="space-y-4">
                {hasUnsavedChanges && (
                  <div className="text-xs text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-950/10 border border-red-200 dark:border-red-800 rounded px-2 py-1.5">
                    ⚠️ The link below uses saved access settings. Save your changes above to apply them.
                  </div>
                )}
                <div>
                  <div className="text-sm text-muted-foreground mb-1">Public link</div>
                  <div className="flex gap-2">
                    <input readOnly value={link} className="flex-1 px-3 py-2 border rounded bg-background text-foreground border-input" />
                    <button
                      onClick={() => copyToClipboard(link, 'link')}
                      className="px-3 py-2 text-muted-foreground hover:text-foreground border border-input focus:ring-2 focus:ring-ring focus:border-ring bg-background text-foreground rounded hover:bg-muted transition-colors"
                      title="Copy link"
                    >
                      {copiedLink ? (
                        <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                      ) : (
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                        </svg>
                      )}
                    </button>
                  </div>
                </div>
                <div>
                  <div className="text-sm text-muted-foreground mb-1">Embed (iframe)</div>
                  <div className="flex gap-2">
                    <textarea readOnly value={iframe} rows={6} className="flex-1 px-3 py-2 border rounded font-mono text-xs bg-background text-foreground border-input" />
                    <button
                      onClick={() => copyToClipboard(iframe, 'embed')}
                      className="px-3 py-2 text-muted-foreground hover:text-foreground border border-input focus:ring-2 focus:ring-ring focus:border-ring bg-background text-foreground rounded hover:bg-muted transition-colors self-start"
                      title="Copy embed code"
                    >
                      {copiedEmbed ? (
                        <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                      ) : (
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                        </svg>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              <div className="text-sm text-muted-foreground">Publish the form to generate a public link.</div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default FormShareModal;


